/**
 * Phase 7.5 — Active rollover descendant guard for manual split.
 *
 * Doctrine:
 *   - Bucket 4 §4.3   A ticket with active rollover descendants already
 *                     has unresolved continuation lineage. Splitting it
 *                     again can corrupt:
 *                       - rollover chain truth
 *                       - Due Out risk
 *                       - split group identity
 *                       - merge / reconciliation history
 *                       - Part X of Y UI
 *                       - technician responsibility
 *                       - EOD continuation tracking
 *   - Bucket 4 §4.3   Before allowing a new split, the rollover chain
 *                     must be resolved, merged, or reconciled.
 *   - Bucket 3 §3.4   `parking_lot_inactive` is NOT active production
 *                     work (no dispatch plan); a descendant in that
 *                     state does not block the split.
 *   - Bucket 4.5 §4.5.x  Phase 5.5 already validates Due Out for
 *                     split creation via `validateDueOutForSplitCreation`
 *                     with the `active_rollover_descendant_blocks_split`
 *                     blocker. Phase 7.5 canonicalizes the classifier
 *                     so both edge functions and Phase 5.5 agree.
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 7.5 confirmed BOTH split edge functions
 * already carry an inline guard:
 *
 *   - `supabase/functions/split-ticket/index.ts:80-93` (R43 FIX-6)
 *   - `supabase/functions/execute-manual-split/index.ts:164-178` (CORE24-M-3)
 *
 * Both queries use the same SQL pattern:
 *   `.or('rollover_of_ticket_id.eq.X,parent_ticket_id.eq.X')`
 *   `.not('status', 'in', '("Completed","Cancelled","Deleted")')`
 *
 * Differences are minor (manual split adds `.neq('id', originalTicketId)`
 * for self-exclusion). The Phase 7.5 contribution is:
 *
 *   1. A canonical pure classifier so any future preflight (UI modal,
 *      client-side check, board-level warning, board-issue detector)
 *      consumes the same doctrine the SQL guards already enforce.
 *   2. Vocabulary for the specific blocker channels (active /
 *      running / paused / held / scheduled-but-parked / held-in-parking).
 *   3. Source-scan tests pinning that BOTH edge-function guards remain
 *      present and use the doctrinal SQL filter.
 *
 * Phase 7.5 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections.
 *   - No DB migration / no schema change.
 *   - No edge-function rewrite. The existing inline guards stay
 *     verbatim; the helper supplies the canonical doctrine + tests.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState` (descendant state classification).
 *   - Phase 1.4 `canPerformActiveProductionMutation` (mutation gate).
 *   - Phase 1.3 `isProductionRelevantState` (active scheduled work set).
 *   - Phase 5.5 `validateDueOutForSplitCreation`
 *     (`active_rollover_descendant_blocks_split` blocker; this helper
 *     supplies the boolean `hasActiveRolloverDescendant` it consumes).
 *   - Phase 7.1 `isRolloverContinuationNotJobLineSplit` (rollover
 *     vs job-line distinction).
 *   - Phase 7.4 `partialCompletionMerge` (merge-truth doctrine).
 */

import {
  getProductionState,
  isProductionRelevantState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete blocker codes for the split guard.
 *
 *   - 'active_rollover_descendant_exists'        — at least one
 *      descendant resolves to an active production state.
 *   - 'running_rollover_descendant_exists'       — at least one
 *      descendant currently has a running timer.
 *   - 'paused_rollover_descendant_exists'        — at least one
 *      descendant is paused (work pending).
 *   - 'held_rollover_descendant_exists'          — at least one
 *      descendant is `held_in_bay` or `held_in_parking`.
 *   - 'scheduled_rollover_descendant_exists'     — at least one
 *      descendant is `scheduled` or `scheduled_but_parked`.
 *   - 'unresolved_rollover_chain'                — aggregate code
 *      emitted whenever any of the above fire.
 *   - 'resolve_or_merge_rollover_chain_first'    — actionable
 *      instruction echoed for the UI / writer.
 *   - 'missing_rollover_context'                 — caller supplied
 *      no parent ticket id (defensive; the helper never throws).
 */
export type RolloverDescendantSplitBlocker =
  | 'active_rollover_descendant_exists'
  | 'running_rollover_descendant_exists'
  | 'paused_rollover_descendant_exists'
  | 'held_rollover_descendant_exists'
  | 'scheduled_rollover_descendant_exists'
  | 'unresolved_rollover_chain'
  | 'resolve_or_merge_rollover_chain_first'
  | 'missing_rollover_context';

export const ROLLOVER_DESCENDANT_SPLIT_BLOCKERS = Object.freeze([
  'active_rollover_descendant_exists',
  'running_rollover_descendant_exists',
  'paused_rollover_descendant_exists',
  'held_rollover_descendant_exists',
  'scheduled_rollover_descendant_exists',
  'unresolved_rollover_chain',
  'resolve_or_merge_rollover_chain_first',
  'missing_rollover_context',
] as const) satisfies ReadonlyArray<RolloverDescendantSplitBlocker>;

/**
 * Discrete descendant classification states. Composed from Phase 1.1
 * production states + the descendant's `status` + `timer_status`.
 *
 *   - 'active_unresolved'       — production-state is active-scheduled
 *      OR active-in-bay OR held-in-bay etc. (the production-relevant
 *      set per Phase 1.3, MINUS `parking_lot_inactive`).
 *   - 'running'                 — `timer_status === 'running'`.
 *   - 'paused'                  — `status === 'Paused'` OR
 *      `timer_status === 'paused'`.
 *   - 'held'                    — production-state `held_in_bay` or
 *      `held_in_parking`.
 *   - 'scheduled'               — production-state `scheduled` or
 *      `scheduled_but_parked`.
 *   - 'parking_lot_inactive'    — production-state
 *      `parking_lot_inactive` (no active dispatch plan; resolved /
 *      abandoned; does NOT block split).
 *   - 'completed'               — production-state
 *      `completed_awaiting_pickup`.
 *   - 'picked_up'               — production-state `picked_up`.
 *   - 'cancelled'               — `status === 'Cancelled'`.
 *   - 'deleted'                 — production-state `deleted` OR
 *      `status === 'Deleted'`.
 *   - 'archived'                — production-state `archived`.
 *   - 'unknown'                 — fall-through (defensive).
 */
export type RolloverDescendantClassification =
  | 'active_unresolved'
  | 'running'
  | 'paused'
  | 'held'
  | 'scheduled'
  | 'parking_lot_inactive'
  | 'completed'
  | 'picked_up'
  | 'cancelled'
  | 'deleted'
  | 'archived'
  | 'unknown';

export const ROLLOVER_DESCENDANT_CLASSIFICATIONS = Object.freeze([
  'active_unresolved',
  'running',
  'paused',
  'held',
  'scheduled',
  'parking_lot_inactive',
  'completed',
  'picked_up',
  'cancelled',
  'deleted',
  'archived',
  'unknown',
] as const) satisfies ReadonlyArray<RolloverDescendantClassification>;

/**
 * Discrete "resolved" classifications — states that DO NOT block a
 * fresh split. Frozen so callers can intersect quickly.
 */
const RESOLVED_CLASSIFICATIONS: ReadonlySet<RolloverDescendantClassification> =
  new Set<RolloverDescendantClassification>([
    'parking_lot_inactive',
    'completed',
    'picked_up',
    'cancelled',
    'deleted',
    'archived',
  ]);

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Permissive descendant shape. Extends the Phase 1.1 production-state
 * shape with the lineage + timer columns the classifier reads.
 */
export interface RolloverDescendantShape extends ProductionStateTicketShape {
  id?: string | null;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
  timer_status?: string | null;
}

// =============================================================================
// Lineage predicates
// =============================================================================

/**
 * Is `ticket` a rollover descendant of `parentId`? True iff
 * `rollover_of_ticket_id === parentId` OR `parent_ticket_id ===
 * parentId`. Self-reference (id === parentId) is excluded.
 *
 * Mirrors the SQL predicate used by both edge-function inline guards:
 *
 *   .or('rollover_of_ticket_id.eq.X,parent_ticket_id.eq.X')
 *   .neq('id', X)
 */
export function isRolloverDescendantOf(
  ticket: RolloverDescendantShape,
  parentId: string,
): boolean {
  if (!ticket || !parentId) return false;
  if (ticket.id === parentId) return false;
  return (
    ticket.rollover_of_ticket_id === parentId ||
    ticket.parent_ticket_id === parentId
  );
}

/**
 * Enumerate all rollover descendants of `parentId` in a tickets array.
 * Caller passes in the candidate set; the helper does not query the
 * DB.
 */
export function getRolloverDescendants(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): RolloverDescendantShape[] {
  if (!parentId) return [];
  const out: RolloverDescendantShape[] = [];
  for (const t of tickets) {
    if (isRolloverDescendantOf(t, parentId)) out.push(t);
  }
  return out;
}

// =============================================================================
// Classifier
// =============================================================================

/**
 * Classify a single descendant's resolution state. Pure; the
 * classifier reads only the descendant's fields, never the DB.
 *
 * Resolution order (terminal-first; first match wins):
 *
 *   1. status === 'Cancelled'                              → 'cancelled'
 *   2. production-state 'deleted' or status 'Deleted'      → 'deleted'
 *   3. production-state 'archived'                         → 'archived'
 *   4. production-state 'picked_up'                        → 'picked_up'
 *   5. production-state 'completed_awaiting_pickup'        → 'completed'
 *   6. production-state 'parking_lot_inactive'             → 'parking_lot_inactive'
 *   7. timer_status === 'running'                          → 'running'
 *   8. timer_status === 'paused' OR status === 'Paused'    → 'paused'
 *   9. production-state 'held_in_bay' or 'held_in_parking' → 'held'
 *  10. production-state 'scheduled' or 'scheduled_but_parked' → 'scheduled'
 *  11. production-state is production-relevant + active-in-bay → 'active_unresolved'
 *  12. otherwise                                           → 'unknown'
 */
export function classifyRolloverDescendantState(
  descendant: RolloverDescendantShape,
): RolloverDescendantClassification {
  const status = descendant.status ?? null;
  const productionState: ProductionState = getProductionState(descendant);

  // 1-3: cancelled / deleted / archived terminals
  if (status === 'Cancelled') return 'cancelled';
  if (productionState === 'deleted' || status === 'Deleted') return 'deleted';
  if (productionState === 'archived') return 'archived';

  // 4-5: picked_up / completed
  if (productionState === 'picked_up') return 'picked_up';
  if (productionState === 'completed_awaiting_pickup') return 'completed';

  // 6: parking_lot_inactive (no dispatch plan → resolved)
  if (productionState === 'parking_lot_inactive') return 'parking_lot_inactive';

  // 7-8: timer states
  if (descendant.timer_status === 'running') return 'running';
  if (descendant.timer_status === 'paused' || status === 'Paused') return 'paused';

  // 9: held
  if (productionState === 'held_in_bay' || productionState === 'held_in_parking') {
    return 'held';
  }

  // 10: scheduled
  if (productionState === 'scheduled' || productionState === 'scheduled_but_parked') {
    return 'scheduled';
  }

  // 11: active production-relevant fall-through (e.g., active_in_bay)
  if (isProductionRelevantState(productionState)) {
    return 'active_unresolved';
  }

  // 12: defensive fallback
  return 'unknown';
}

/**
 * Filter the descendant list to only those in an UNRESOLVED state.
 * Sister to `getRolloverDescendants` that strips the resolved
 * classifications.
 */
export function getActiveRolloverDescendants(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): RolloverDescendantShape[] {
  const all = getRolloverDescendants(parentId, tickets);
  return all.filter(t => !RESOLVED_CLASSIFICATIONS.has(classifyRolloverDescendantState(t)));
}

/**
 * Boolean wrapper: is there at least one active (unresolved)
 * rollover descendant for `parentId`?
 *
 * The exact predicate the Phase 5.5
 * `validateDueOutForSplitCreation.hasActiveRolloverDescendant` flag
 * consumes.
 */
export function hasActiveRolloverDescendants(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): boolean {
  return getActiveRolloverDescendants(parentId, tickets).length > 0;
}

// =============================================================================
// Blocker emission
// =============================================================================

/**
 * Compute the canonical blocker set for a proposed split given the
 * parent ticket id and the candidate descendant list. Pure; collects
 * (does not short-circuit) so callers see every channel that applies.
 *
 * Returns:
 *   - `[]` when no descendants block the split.
 *   - Otherwise: ['unresolved_rollover_chain',
 *      'active_rollover_descendant_exists',
 *      'resolve_or_merge_rollover_chain_first', ...]
 *     plus per-classification codes (`running_*`, `paused_*`, etc.).
 *
 * When `parentId` is missing, emits `missing_rollover_context` and
 * no further blockers.
 */
export function getRolloverDescendantSplitBlockers(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): RolloverDescendantSplitBlocker[] {
  if (!parentId) return ['missing_rollover_context'];

  const active = getActiveRolloverDescendants(parentId, tickets);
  if (active.length === 0) return [];

  const blockers: RolloverDescendantSplitBlocker[] = [];
  const push = (b: RolloverDescendantSplitBlocker) => {
    if (!blockers.includes(b)) blockers.push(b);
  };

  // Always emit the aggregate codes when ANY active descendant exists.
  push('unresolved_rollover_chain');
  push('active_rollover_descendant_exists');
  push('resolve_or_merge_rollover_chain_first');

  // Per-classification breakdown.
  let hasRunning = false;
  let hasPaused = false;
  let hasHeld = false;
  let hasScheduled = false;

  for (const d of active) {
    const c = classifyRolloverDescendantState(d);
    if (c === 'running') hasRunning = true;
    else if (c === 'paused') hasPaused = true;
    else if (c === 'held') hasHeld = true;
    else if (c === 'scheduled') hasScheduled = true;
  }

  if (hasRunning) push('running_rollover_descendant_exists');
  if (hasPaused) push('paused_rollover_descendant_exists');
  if (hasHeld) push('held_rollover_descendant_exists');
  if (hasScheduled) push('scheduled_rollover_descendant_exists');

  return blockers;
}

/**
 * Boolean: can the proposed split proceed without violating the
 * rollover-descendant doctrine? Sister to
 * `hasActiveRolloverDescendants` returning the inverse.
 */
export function canSplitWithRolloverDescendants(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): boolean {
  return getRolloverDescendantSplitBlockers(parentId, tickets).length === 0;
}

/**
 * Convenience: throw a descriptive Error when the split would violate
 * the doctrine. The error message echoes the same string both edge
 * functions return to the client today, so adoption is API-compatible.
 */
export function assertNoActiveRolloverDescendantsBeforeSplit(
  parentId: string | null | undefined,
  tickets: ReadonlyArray<RolloverDescendantShape>,
): void {
  const blockers = getRolloverDescendantSplitBlockers(parentId, tickets);
  if (blockers.length === 0) return;
  throw new Error(
    'Cannot split ticket with active rollover descendants. Complete or cancel rollover parts first.',
  );
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. Edge-function adoption (deferred):
//    Both `supabase/functions/split-ticket/index.ts:80-93` and
//    `supabase/functions/execute-manual-split/index.ts:164-178` carry
//    the inline SQL guard today. Future adoption can mirror this
//    helper into `supabase/functions/_shared/rolloverDescendantGuard.ts`
//    (Deno-compatible duplicate) and replace the inline filter with a
//    call to `assertNoActiveRolloverDescendantsBeforeSplit` after a
//    light SELECT of the descendant rows. The Phase 7.5 source-scan
//    tests pin the inline guard so dedup is safe.
//
// 2. Client-side preflight:
//    The OptiPlaceModal / SkillSplit / OptiBoard split UIs can call
//    `hasActiveRolloverDescendants(ticketId, boardTickets)` before
//    showing the split modal so users see the blocker reason
//    immediately (no server round-trip). Phase 7.3's board-level
//    ticket map provides the right input set.
//
// 3. Phase 5.5 integration:
//    `validateDueOutForSplitCreation` accepts a
//    `hasActiveRolloverDescendant?: boolean` flag. Callers should
//    derive that flag from THIS helper rather than computing it
//    inline.
//
// 4. Board-issue detector:
//    A new `BoardIssueType.UNRESOLVED_ROLLOVER_CHAIN` could surface
//    when a ticket has active rollover descendants AND a split
//    invocation is attempted. Out of Phase 7.5 scope; helper-only.
//
// 5. Audit:
//    Reserve `SPLIT_BLOCKED_ROLLOVER_DESCENDANTS` in
//    `AdminActionType` when the edge-function guards begin emitting
//    audit rows on rejected splits. Today they only return 400.
